import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "bonker_cookie_consent";
export const OPEN_COOKIE_PREFS_EVENT = "open-cookie-preferences";
export const COOKIE_CONSENT_CHANGED = "cookie-consent-changed";

interface Consent {
  necessary: true;
  analytics: boolean;
  ts: string;
}

export function getCookieConsent(): Consent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Consent) : null;
  } catch {
    return null;
  }
}

/** Whether the user granted consent for analytics cookies. */
export function hasAnalyticsConsent(): boolean {
  return getCookieConsent()?.analytics === true;
}

export const CookieConsent: React.FC = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!getCookieConsent()) setOpen(true);
  }, []);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(OPEN_COOKIE_PREFS_EVENT, handler);
    return () => window.removeEventListener(OPEN_COOKIE_PREFS_EVENT, handler);
  }, []);

  const decide = useCallback((analytics: boolean) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ necessary: true, analytics, ts: new Date().toISOString() }),
      );
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_CHANGED));
    setOpen(false);
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed bottom-0 inset-x-0 z-[60] p-3 sm:p-4 pointer-events-none"
          role="dialog"
          aria-label="Informativa cookie"
          aria-live="polite"
          data-testid="cookie-banner"
        >
          <div className="max-w-sm mx-auto sm:mr-4 sm:ml-auto bg-background border border-white/15 shadow-2xl pointer-events-auto">
            <div className="p-4">
              <div className="flex items-start gap-2.5 mb-3">
                <Cookie className="h-5 w-5 text-secondary shrink-0" />
                <div>
                  <p className="font-display font-bold text-base leading-tight">Cookie</p>
                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                    Usiamo cookie tecnici necessari al funzionamento del sito e, solo con
                    il tuo consenso, cookie di analisi statistica per capire come viene
                    usato il sito. Dettagli nella{" "}
                    <Link to="/cookie-policy" className="text-secondary underline hover:no-underline">Cookie Policy</Link>.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="cookie-reject-all"
                  onClick={() => decide(false)}
                  className="flex-1 border border-white/20 text-foreground font-bold uppercase tracking-[0.1em] text-xs py-3 hover:border-white/50 transition-colors"
                >
                  Solo necessari
                </button>
                <button
                  data-testid="cookie-accept-all"
                  onClick={() => decide(true)}
                  className="flex-1 bg-primary text-primary-foreground font-bold uppercase tracking-[0.1em] text-xs py-3 hover:-translate-y-[2px] transition-transform"
                >
                  Accetta
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
